import React, { useContext, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Modal,
  Form
} from 'react-bootstrap';
import { storesContext } from '../../../stores/storesContext';
import { useGreenAtlasBundles } from '../GreenAtlasBundles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrashAlt,
  faSync,
  faEdit,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

const GreenAtlasBundleManager = () => {
  const userStore = useContext(storesContext);
  const selectedPropertyIdentifier =
    userStore.propertiesStore.selectedProperty?.identifier;
  const selectedDatabase = userStore.selectedDatabase;
  const bearerToken = userStore.bearerToken;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [newBundleName, setNewBundleName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    bundles,
    loadingBundles,
    error,
    deleteBundle,
    updateBundleName,
    refreshBundles
  } = useGreenAtlasBundles(
    selectedPropertyIdentifier,
    selectedDatabase,
    bearerToken
  );

  const handleDeleteClick = (bundle) => {
    setSelectedBundle(bundle);
    setShowDeleteModal(true);
  };

  const handleEditClick = (bundle) => {
    setSelectedBundle(bundle);
    setNewBundleName(bundle.name || '');
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (!selectedBundle) return;

    setIsProcessing(true);
    const success = await deleteBundle(selectedBundle.property_bundle_id);
    setIsProcessing(false);

    if (success) {
      setShowDeleteModal(false);
      setSelectedBundle(null);
    }
  };

  const handleUpdate = async () => {
    if (!selectedBundle) return;

    setIsProcessing(true);
    const success = await updateBundleName(
      selectedBundle.property_bundle_id,
      newBundleName
    );
    setIsProcessing(false);

    if (success) {
      setShowEditModal(false);
      setSelectedBundle(null);
    }
  };

  return (
    <div className='green-atlas-bundle-manager'>
      {error && (
        <Row className='mb-3'>
          <Col>
            <Alert variant='danger'>{error}</Alert>
          </Col>
        </Row>
      )}

      <Alert variant='info' className='mb-3'>
        <FontAwesomeIcon icon={faInfoCircle} className='me-2' /> Current
        Property ID: <strong>{selectedPropertyIdentifier}</strong>
      </Alert>

      <Row className='mb-3'>
        <Col>
          <Button
            variant='primary'
            onClick={refreshBundles}
            disabled={loadingBundles}
          >
            <FontAwesomeIcon icon={faSync} className='me-2' /> Refresh Bundles
          </Button>
        </Col>
      </Row>
      <hr></hr>

      {loadingBundles ? (
        <div className='text-center my-5'>
          <Spinner animation='border' role='status'></Spinner>
        </div>
      ) : bundles.length > 0 ? (
        <Row>
          {bundles.map((bundle) => (
            <Col md={6} lg={4} key={bundle.property_bundle_id} className='mb-3'>
              <Card>
                <Card.Body>
                  <Card.Title>
                    {bundle.name || (
                      <span className='text-muted font-italic'>
                        Untitled Bundle
                      </span>
                    )}{' '}
                    <small className='text-muted ms-2'>
                      #{bundle.property_bundle_id}
                    </small>
                  </Card.Title>
                  <Card.Text>
                    Uploaded:{' '}
                    {bundle.created_at ? (
                      new Date(bundle.created_at).toLocaleDateString()
                    ) : (
                      <span className='text-muted font-italic'>
                        Unknown date
                      </span>
                    )}
                    <br />
                    Property ID: {selectedPropertyIdentifier}
                  </Card.Text>
                  <div className='d-flex'>
                    <Button
                      variant='outline-primary'
                      size='sm'
                      onClick={() => handleEditClick(bundle)}
                      className='mr-2'
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </Button>
                    <Button
                      variant='danger'
                      size='sm'
                      onClick={() => handleDeleteClick(bundle)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} /> Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Alert variant='info'>
          No bundles found. Upload a Green Atlas bundle to get started.
        </Alert>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the bundle "
          {selectedBundle?.name || 'Untitled Bundle'}"? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => setShowDeleteModal(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant='danger'
            onClick={handleDelete}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Name Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Bundle Name</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Bundle Name</Form.Label>
              <Form.Control
                type='text'
                value={newBundleName}
                onChange={(e) => setNewBundleName(e.target.value)}
                placeholder='Enter bundle name'
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => setShowEditModal(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleUpdate}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GreenAtlasBundleManager;

